import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, applyOps, getWorkspace } from "@/api/client";
import type { Workspace, WorkspaceOp } from "@/api/client";
import { inverseOf } from "@/flow/workspace";

export interface WorkspaceState {
  ws: Workspace | null;
  loaded: boolean;
  error: string | null;
  busy: boolean;
  /** Apply operations, in order, all or none. Resolves false when the API refused them. */
  run: (ops: WorkspaceOp[]) => Promise<boolean>;
  /** Undo the last batch, when every operation in it can be undone. */
  undo: () => Promise<boolean>;
  canUndo: boolean;
  reload: () => Promise<void>;
}

/**
 * One sheet's workspace. Batches are sent one at a time, in the order they were asked
 * for, so a quick run of keystrokes lands as typed. Undo is an operation too: each
 * batch's inverse is kept, and a batch that cannot be undone (an approval, a grouping)
 * ends what undo can reach.
 */
export function useWorkspace(runId: string, pageIndex: number | null): WorkspaceState {
  const [ws, setWs] = useState<Workspace | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [undoDepth, setUndoDepth] = useState(0);
  const current = useRef<Workspace | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const undos = useRef<WorkspaceOp[][]>([]);

  const reload = useCallback(async () => {
    if (pageIndex === null) {
      return;
    }
    try {
      const got = await getWorkspace(runId, pageIndex);
      current.current = got;
      setWs(got);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "could not load the sheet");
    } finally {
      setLoaded(true);
    }
  }, [runId, pageIndex]);

  useEffect(() => {
    setLoaded(false);
    setWs(null);
    current.current = null;
    undos.current = [];
    setUndoDepth(0);
    void reload();
  }, [reload]);

  const send = useCallback(
    (ops: WorkspaceOp[], record: boolean): Promise<boolean> => {
      const next = queue.current.then(async () => {
        const before = current.current;
        if (pageIndex === null || !before || !ops.length) {
          return false;
        }
        setBusy(true);
        try {
          const after = await applyOps(runId, pageIndex, ops);
          current.current = after;
          setWs(after);
          setError(null);
          if (record) {
            // Each op's inverse is worked out against the sheet just before it; within a
            // batch only the last op sees `before` exactly, so a batch is undoable only
            // when it is one op, or when every inverse needs nothing from the sheet.
            const inverses = ops.map((op) => inverseOf(op, before, after));
            if (inverses.every((i): i is WorkspaceOp => i !== null)) {
              undos.current.push(inverses.reverse());
            } else {
              undos.current = [];
            }
            setUndoDepth(undos.current.length);
          }
          return true;
        } catch (err) {
          setError(err instanceof ApiError ? err.detail : "the change was not saved");
          return false;
        } finally {
          setBusy(false);
        }
      });
      queue.current = next;
      return next;
    },
    [runId, pageIndex],
  );

  const run = useCallback((ops: WorkspaceOp[]) => send(ops, true), [send]);

  const undo = useCallback(async () => {
    const last = undos.current.pop();
    setUndoDepth(undos.current.length);
    if (!last) {
      return false;
    }
    return send(last, false);
  }, [send]);

  return { ws, loaded, error, busy, run, undo, canUndo: undoDepth > 0, reload };
}
